import { Button, Cell, FormControl, Grid, HeadingSection, HFlow } from 'bold-ui'
import { useAlert } from 'components/alert'
import { CheckboxField, MunicipioSelectField, TipoLogradouroSelectField, UfSelectField } from 'components/form'
import { FormRenderProps } from 'components/form/core'
import { resolveValue } from 'components/form/core/hooks/useField'
import {
  BairroSelectField,
  BairroSelectFieldModel,
  LogradouroSelectField,
  LogradouroSelectModel,
} from 'components/form/field'
import { css } from 'emotion'
import { useApolloClient } from 'graphql/hooks'
import { EnderecoDocument } from 'graphql/hooks.generated'
import { EnderecoQuery, EnderecoQueryVariables } from 'graphql/types.generated'
import React, { Fragment, useRef } from 'react'
import { onlyNumbers } from 'util/mask'
import { MetaPath } from 'util/metaPath'
import { cepRule } from 'util/validation'
import { PAIS_BRASIL } from 'view/cidadao/create/types/model-cidadao'

import { TextField } from '../../core/TextField'
import { CepField } from '../CepField'
import { MunicipioSelectModel } from '../select/MunicipioSelectField'
import { PaisSelectField, PaisSelectFieldModel } from '../select/PaisSelectField'
import { TipoLogradouroSelectModel } from '../select/TipoLogradouroSelectField'
import { UfSelectModel } from '../select/UfSelectField'

export type AutoCompleteOptions = 'none' | 'on'

export interface EnderecoFieldGroupModel {
  bairro?: BairroSelectFieldModel
  cep?: string
  complemento?: string
  logradouro: LogradouroSelectModel
  municipio?: MunicipioSelectModel
  numero?: string
  pontoReferencia?: string
  semNumero?: boolean
  tipoLogradouro?: TipoLogradouroSelectModel
  uf?: UfSelectModel
  pais?: PaisSelectFieldModel
  municipioResidenciaExterior?: string
  area?: string
  microArea?: string
  foraArea?: boolean
  numeroFamilia?: string
}

export interface EnderecoFieldGroupProps {
  name: MetaPath<EnderecoFieldGroupModel>
  formProps: FormRenderProps<any>
  formModelValue?: any
  renderPais?: boolean
  renderAreaMicroArea?: boolean
  renderHeading?: boolean
  isCepRequired?: boolean
  autoComplete?: AutoCompleteOptions
  isCidadaoAldeado?: boolean
  renderTipoLogradouro?: boolean
}

export function EnderecoFieldGroup(props: EnderecoFieldGroupProps) {
  const {
    name,
    formProps,
    formModelValue,
    renderPais,
    renderAreaMicroArea,
    renderHeading,
    isCepRequired,
    autoComplete = 'none',
    renderTipoLogradouro,
  } = props
  const alert = useAlert()
  const values =
    resolveValue(formModelValue ? formModelValue : formProps.values, name) || ({} as EnderecoFieldGroupModel)

  const ufInputRef = useRef<HTMLInputElement>()
  const bairroInputRef = useRef<HTMLInputElement>()
  const numeroInputRef = useRef<HTMLInputElement>()

  const client = useApolloClient()
  const buscarCep = (variables: EnderecoQueryVariables) => {
    return client
      .query<EnderecoQuery>({ query: EnderecoDocument, variables })
      .then((response) => response.data)
  }

  const clearEstado = () => {
    formProps.form.change(name.municipio.absolutePath(), null)
    formProps.form.change(name.logradouro.absolutePath(), null)
    clearMunicipio()
  }

  const clearMunicipio = () => {
    formProps.form.change(name.bairro.absolutePath(), null)
    formProps.form.change(name.logradouro.absolutePath(), null)
    clearBairro()
  }

  const clearBairro = () => {
    formProps.form.change(name.tipoLogradouro.absolutePath(), null)
    formProps.form.change(name.logradouro.absolutePath(), null)
    formProps.form.change(name.numero.absolutePath(), null)
    formProps.form.change(name.semNumero.absolutePath(), false)
    formProps.form.change(name.complemento.absolutePath(), null)
    formProps.form.change(name.pontoReferencia.absolutePath(), null)
  }

  const handleBuscarCepKeyDownEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      if (values.cep?.trim()) {
        handleBuscarCep()
      } else {
        alert('warning', 'Insira um CEP válido antes de pesquisar.')
      }
    }
  }

  const handleBuscarCep = async () => {
    if (cepRule(values.cep)) {
      alert('warning', 'Insira um CEP válido antes de pesquisar.')
      return
    }
    const response: EnderecoQuery = await buscarCep({ cep: values.cep })
    if (response && response.endereco) {
      const municipio: MunicipioSelectModel = { ...response.endereco.municipio, uf: response.endereco.uf }
      const bairro: BairroSelectFieldModel = response.endereco.bairro
      const logradouro = response.endereco && response.endereco.logradouro

      formProps.form.change(name.uf.absolutePath(), response.endereco.uf)
      formProps.form.change(name.municipio.absolutePath(), municipio)
      formProps.form.change(name.bairro.absolutePath(), bairro)
      formProps.form.change(name.tipoLogradouro.absolutePath(), response.endereco.tipoLogradouro)
      logradouro && formProps.form.change(name.logradouro.absolutePath(), { nome: logradouro.toUpperCase() })

      if (!response.endereco.bairro) {
        bairroInputRef.current.focus()
      } else {
        numeroInputRef.current.focus()
      }
    } else {
      alert('warning', 'CEP não encontrado.')
      ufInputRef.current.focus()
    }
  }

  const isRequired = Boolean(
    values.cep ||
      values.uf ||
      values.municipio ||
      values.bairro ||
      values.tipoLogradouro ||
      values.logradouro ||
      values.numero ||
      values.semNumero
  )

  const fillLogradouro = () => {
    if (values.logradouro?.id) {
      formProps.form.change(name.cep.absolutePath(), values.logradouro.cep)
      formProps.form.change(name.bairro.absolutePath(), values.logradouro.bairro)
      formProps.form.change(name.tipoLogradouro.absolutePath(), values.logradouro.tipoLogradouro)
    }
  }

  return (
    <Fragment>
      {renderHeading && <HeadingSection level={2} title='Endereço' />}
      <Grid wrap>
        {renderPais && (
          <Cell size={6}>
            <PaisSelectField name={name.pais} label='País de residência' required />
          </Cell>
        )}
        {renderPais && values.pais?.nome !== PAIS_BRASIL.nome ? (
          <Cell size={6}>
            <TextField name={name.municipioResidenciaExterior} uppercase label='Município de residência' required />
          </Cell>
        ) : (
          <>
            <Cell size={6}>
              <HFlow>
                <CepField
                  name={name.cep}
                  label='CEP'
                  required={isCepRequired}
                  autoComplete={autoComplete}
                  onKeyDownCapture={handleBuscarCepKeyDownEnter}
                />
                <FormControl label={<span>&nbsp;</span>}>
                  <Button onClick={values.cep ? handleBuscarCep : null} size='small' type='button' kind='primary'>
                    Pesquisar
                  </Button>
                </FormControl>
              </HFlow>
            </Cell>
            {!renderPais && <Cell size={6} />}
            <Cell size={3}>
              <UfSelectField
                name={name.uf}
                label='UF'
                onChange={clearEstado}
                required={isRequired}
                inputRef={ufInputRef}
                autoComplete={autoComplete}
              />
            </Cell>
            <Cell size={3}>
              <MunicipioSelectField
                name={name.municipio}
                disabled={!values.uf}
                label='Município'
                ufId={values.uf?.id}
                onChange={clearMunicipio}
                required={isRequired}
                autoComplete={autoComplete}
              />
            </Cell>
            <Cell size={4}>
              <BairroSelectField
                name={name.bairro}
                label='Bairro'
                onChange={clearBairro}
                municipioId={values.municipio?.id}
                ufId={values.uf?.id}
                required={isRequired}
                inputRef={bairroInputRef}
                disabled={!values.municipio}
                autoComplete={autoComplete}
              />
            </Cell>
            <Cell size={2} />
            {renderTipoLogradouro && (
              <Cell size={2}>
                <TipoLogradouroSelectField
                  name={name.tipoLogradouro}
                  label='Tipo de logradouro'
                  required={isRequired}
                  disabled={!values.bairro || !values.bairro.nome}
                  autoComplete={autoComplete}
                />
              </Cell>
            )}
            <Cell size={renderTipoLogradouro ? 4 : 6}>
              <LogradouroSelectField
                name={name.logradouro}
                label='Logradouro'
                disabled={!values.bairro || !values.bairro.nome}
                required={isRequired}
                municipioId={values.municipio && values.municipio.id}
                bairroId={values.bairro && values.bairro.id}
                tipoLogradouroId={values.tipoLogradouro && values.tipoLogradouro.id}
                onSelect={fillLogradouro}
                autoComplete={autoComplete}
              />
            </Cell>
            <Cell size={6}>
              <HFlow>
                <TextField
                  name={name.numero}
                  label='Número'
                  disabled={values.semNumero || !values.bairro || !values.bairro.nome}
                  inputRef={numeroInputRef}
                  maxLength={7}
                  required={isRequired}
                  autoComplete={autoComplete}
                />
                <FormControl label={<span>&nbsp;</span>}>
                  <CheckboxField
                    name={name.semNumero}
                    label='Sem número'
                    style={css`
                      margin-top: 0.25rem;
                    `}
                    required={isRequired}
                    disabled={!values.bairro || !values.bairro.nome}
                  />
                </FormControl>
              </HFlow>
            </Cell>
            <Cell size={6}>
              <TextField
                uppercase
                name={name.complemento}
                label='Complemento'
                disabled={!values.bairro || !values.bairro.nome}
                autoComplete={autoComplete}
              />
            </Cell>
            <Cell size={6}>
              <TextField
                uppercase
                name={name.pontoReferencia}
                label='Ponto de referência'
                disabled={!values.bairro || !values.bairro.nome}
                autoComplete={autoComplete}
              />
            </Cell>
            {renderAreaMicroArea && (
              <>
                <Cell size={3}>
                  <TextField
                    name={name.area}
                    parse={onlyNumbers}
                    maxLength={3}
                    label='Área'
                    disabled={values.foraArea}
                  />
                </Cell>
                <Cell size={3}>
                  <TextField
                    name={name.microArea}
                    parse={onlyNumbers}
                    maxLength={2}
                    label='Microárea'
                    disabled={values.foraArea}
                    autoComplete={autoComplete}
                  />
                </Cell>
                <Cell size={6}>
                  <FormControl label={<span>&nbsp;</span>}>
                    <CheckboxField name={name.foraArea} label='Fora da área' style={{ marginTop: '0.25rem' }} />
                  </FormControl>
                </Cell>
              </>
            )}
          </>
        )}
      </Grid>
    </Fragment>
  )
}

export const convertLogradouro = (logradouro: LogradouroSelectModel) => {
  const nome = logradouro?.nome
  const tituloPatente = logradouro?.tituloPatente?.nome

  return tituloPatente ? `${tituloPatente} ${nome}` : nome
}
